import { useState, useEffect, useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, ChevronsUpDown, LayoutGrid, MessageSquareWarning, ClipboardList } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ScoreEntete } from '../components/qualiopi/ScoreEntete';
import { RecapNotesAudit, RappelAudit } from '../components/qualiopi/RecapNotesAudit';
import { CritereBloc } from '../components/qualiopi/CritereBloc';
import { IndicateurPanneau } from '../components/qualiopi/IndicateurPanneau';
import { RegistreReclamations } from '../components/qualiopi/reclamations/RegistreReclamations';

const ONGLETS = ['indicateurs', 'reclamations', 'notes-audit'];
// Le plan d'amélioration et les risques ont été déplacés dans Administratif → Amélioration continue
const DEPLACES = { amelioration: '/administratif/amelioration-continue', risques: '/administratif/amelioration-continue?onglet=risques' };

const Pastille = ({ n, testId }) => (n > 0 ? <span className="ml-1.5 rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white" data-testid={testId}>{n}</span> : null);

const Indicateurs = ({ data, onOpen }) => {
  const [ouverts, setOuverts] = useState([]);
  const basculer = (n) => setOuverts((o) => (o.includes(n) ? o.filter((x) => x !== n) : [...o, n]));
  const toutOuvert = ouverts.length === data.criteres.length;
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setOuverts(toutOuvert ? [] : data.criteres.map((c) => c.num))} data-testid="qualiopi-tout-deplier">
          <ChevronsUpDown className="h-4 w-4 mr-1" /> {toutOuvert ? 'Tout replier' : 'Tout déplier'}
        </Button>
      </div>
      {data.criteres.map((c) => (
        <CritereBloc key={c.num} critere={c} indicateurs={data.indicateurs.filter((i) => i.critere === c.num)}
          ouvert={ouverts.includes(c.num)} onToggle={() => basculer(c.num)} onOpen={onOpen} />
      ))}
    </div>
  );
};

export default function Qualiopi() {
  const [data, setData] = useState(null);
  const [indicateur, setIndicateur] = useState(null);
  const [params, setParams] = useSearchParams();
  const onglet = ONGLETS.includes(params.get('onglet')) ? params.get('onglet') : 'indicateurs';

  const charger = useCallback(() => {
    axios.get('/qualiopi').then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || 'Impossible de charger Qualiopi'));
  }, []);
  useEffect(() => { charger(); }, [charger]);
  const fermer = useCallback(() => setIndicateur(null), []);

  if (DEPLACES[params.get('onglet')]) return <Navigate to={DEPLACES[params.get('onglet')]} replace />;
  if (!data) return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="qualiopi-page">
      <div>
        <h1 className="text-3xl font-display font-bold text-[#0F172A] flex items-center gap-3"><ShieldCheck className="h-8 w-8 text-[#8DC63F]" /> Qualiopi</h1>
        <p className="text-slate-500 mt-1">32 indicateurs du référentiel national qualité. Un indicateur passe au vert quand il a au moins une preuve et que vous le validez.</p>
      </div>
      <ScoreEntete data={data} onChanged={charger} />
      <RappelAudit onOpen={setIndicateur} />
      <Tabs value={onglet} onValueChange={(v) => setParams(v === 'indicateurs' ? {} : { onglet: v })}>
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1 sm:w-auto">
          <TabsTrigger value="indicateurs" data-testid="qualiopi-onglet-indicateurs"><LayoutGrid className="h-4 w-4 mr-1.5" /> Indicateurs</TabsTrigger>
          <TabsTrigger value="reclamations" data-testid="qualiopi-onglet-reclamations">
            <MessageSquareWarning className="h-4 w-4 mr-1.5" /> Réclamations <Pastille n={data.compteurs?.reclamations_nouvelles} testId="qualiopi-reclamations-nouvelles" />
          </TabsTrigger>
          <TabsTrigger value="notes-audit" data-testid="qualiopi-onglet-notes-audit"><ClipboardList className="h-4 w-4 mr-1.5" /> Notes d'audit</TabsTrigger>
        </TabsList>
        <TabsContent value="notes-audit" className="mt-4"><RecapNotesAudit onOpen={setIndicateur} /></TabsContent>
        <TabsContent value="indicateurs" className="mt-4"><Indicateurs data={data} onOpen={setIndicateur} /></TabsContent>
        <TabsContent value="reclamations" className="mt-4"><RegistreReclamations onChanged={charger} /></TabsContent>
      </Tabs>
      <IndicateurPanneau num={indicateur} onClose={fermer} onChanged={charger} />
    </div>
  );
}
